import React, { useState } from 'react';
import { Plane, Clock, Calendar, MapPin } from 'lucide-react';

const Flights = () => {
  const [activeTab, setActiveTab] = useState('arrivals');

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50 to-blue-50 pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-coral-100 px-4 py-2 rounded-full mb-4 border border-coral-200">
            <Plane className="w-5 h-5 text-coral-600" />
            <span className="text-coral-700 font-semibold">Flight Information</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            LPIA Flight Arrivals & Departures
          </h1>
          <h2 className="text-2xl text-gray-700 mb-4">
            Lynden Pindling International Airport
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Check flight schedules for Lynden Pindling International Airport. 
            Whether you're meeting arriving guests or planning your own travel, Taxi-98 is ready to serve you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Flight Board Section */}
          <div className="lg:col-span-3">
            {/* Tabs */}
            <div className="flex space-x-2 mb-6">
              <button
                onClick={() => setActiveTab('arrivals')}
                className={`flex-1 px-6 py-4 rounded-lg font-bold text-lg transition-all duration-300 ${
                  activeTab === 'arrivals'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg'
                    : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-cyan-300'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Plane className="w-5 h-5 transform rotate-180" />
                  <span>Arrivals</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('departures')}
                className={`flex-1 px-6 py-4 rounded-lg font-bold text-lg transition-all duration-300 ${
                  activeTab === 'departures'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg'
                    : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-cyan-300'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Plane className="w-5 h-5" />
                  <span>Departures</span>
                </div>
              </button>
            </div>

            {/* Flight Board Embed */}
            <div className="bg-white rounded-3xl shadow-xl border border-cyan-100 overflow-hidden">
              <div className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white p-4">
                <h3 className="text-xl font-bold flex items-center space-x-2">
                  <Clock className="w-5 h-5" />
                  <span>{activeTab === 'arrivals' ? 'Arriving Flights' : 'Departing Flights'}</span>
                </h3>
              </div>

              {/* Placeholder for LPIA Flight Board Embed */}
              <div className="relative bg-gray-50 min-h-[600px] flex items-center justify-center">
                <div className="text-center p-12">
                  <Plane className="w-20 h-20 text-gray-300 mx-auto mb-6" />
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    Coming Soon
                  </h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Real-time flight information from Lynden Pindling International Airport will be available here soon. 
                    For current flight status, please contact LPIA directly or visit their official website.
                  </p>
                  <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 max-w-lg mx-auto">
                    <p className="text-sm text-gray-700">
                      <strong>Integration Instructions:</strong><br />
                      Add the LPIA flight board iframe or widget code here. 
                      Contact LPIA or check their official website for embed options.
                    </p>
                  </div>
                  
                  {/* Example iframe structure (commented out until real URL is available) */}
                  {/* <iframe 
                    src="https://lpia-flight-board-url.com"
                    width="100%"
                    height="600"
                    frameBorder="0"
                    title="LPIA Flight Board"
                    className="w-full"
                  ></iframe> */}
                </div>
              </div>

              <div className="bg-gray-100 p-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 text-center">
                  Flight data provided directly by Lynden Pindling International Airport
                </p>
              </div>
            </div>

            {/* Quick Info */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-xl border border-cyan-200 flex items-center space-x-3">
                <MapPin className="w-6 h-6 text-cyan-600" />
                <div>
                  <p className="text-xs text-gray-600">Airport Code</p>
                  <p className="font-bold text-gray-900">NAS / MYNN</p>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl border border-cyan-200 flex items-center space-x-3">
                <Calendar className="w-6 h-6 text-coral-600" />
                <div>
                  <p className="text-xs text-gray-600">Location</p>
                  <p className="font-bold text-gray-900">Nassau, Bahamas</p>
                </div>
              </div>
              <div className="bg-white p-4 rounded-xl border border-cyan-200 flex items-center space-x-3">
                <Clock className="w-6 h-6 text-green-600" />
                <div>
                  <p className="text-xs text-gray-600">Time Zone</p>
                  <p className="font-bold text-gray-900">EST/EDT</p>
                </div>
              </div>
            </div>
          </div>

          {/* Advertising Reel Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-coral-50 to-coral-100 rounded-3xl shadow-xl border border-coral-200 p-6 sticky top-32">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Taxi-98 Promotions</h3>
              
              {/* Placeholder for advertising reel */}
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-6 border border-coral-300 text-center">
                  <div className="w-16 h-16 bg-coral-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Plane className="w-8 h-8 text-coral-600" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">Coming Soon</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Taxi-98 Promotions — Stay tuned for exclusive offers, travel tips, and partner deals.
                  </p>
                </div>

                {/* Future ad slots */}
                <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl p-6 text-white text-center">
                  <h4 className="font-bold mb-2">Need a Ride?</h4>
                  <p className="text-sm text-cyan-100 mb-4">
                    Book your Taxi-98 pickup from LPIA Airport
                  </p>
                  <button 
                    onClick={() => window.location.href = '/book'}
                    className="bg-white text-cyan-700 px-4 py-2 rounded-lg font-bold hover:bg-cyan-50 transition-all duration-300 w-full"
                  >
                    Book Now
                  </button>
                </div>

                <div className="bg-white rounded-xl p-4 border border-gray-200">
                  <p className="text-xs text-gray-500 text-center">
                    <strong>Advertising Space Available</strong><br />
                    Partner with Taxi-98 to promote your business to travelers
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Flights;
